import { useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import dayjs from 'dayjs';
import type { Dictionary } from 'lodash';

import type { Event } from '../../../Data/types';
import ListHeader from '../ListHeader/ListHeader';
import ListItem from '../ListItem/ListItem';

type Props = {
  events: Dictionary<Event[]>;
  closestDate: string;
};

function formatToDayOfWeek(day: string) {
  return dayjs(day).format('dddd'); // 'dddd' will return the full name of the day of the week
}

function formatToDate(dateStr: string) {
  return dayjs(dateStr).format('MMM DD, YYYY'); // This will return the date in the format you specified
}

function getExpandedPanels(sortedKeys: string[], closestDate: string): string[] {
  if (closestDate && sortedKeys.includes(closestDate)) {
    return [closestDate];
  }

  if (sortedKeys[0]) {
    return [sortedKeys[0]];
  }

  return [];
}

export default function List({ events, closestDate }: Readonly<Props>) {
  const sortedKeys = useMemo(
    () =>
      Object.keys(events).sort((a, b) => {
        return a.localeCompare(b);
      }),
    [events],
  );
  const [expanded, setExpanded] = useState<string[]>(() => getExpandedPanels(sortedKeys, closestDate));
  const togglePanel = (panel: string) => {
    setExpanded((prev) => (prev.includes(panel) ? prev.filter((day) => day !== panel) : [...prev, panel]));
  };
  useEffect(() => {
    const nextExpanded = getExpandedPanels(sortedKeys, closestDate);
    setExpanded((prev) => (prev.length === 1 && prev[0] === nextExpanded[0] ? prev : nextExpanded));
  }, [closestDate, sortedKeys]);
  return (
    <Box>
      {sortedKeys.map((day) => (
        <Accordion
          key={day}
          expanded={expanded.includes(day)}
          onChange={() => togglePanel(day)}
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ListHeader currentDay={formatToDayOfWeek(day)} currentDate={formatToDate(day)} />
          </AccordionSummary>
          <AccordionDetails sx={{ marginBottom: '-2px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {events[day].map((event) => (
                <ListItem key={event.id} event={event} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
